import { useState } from 'react';

import type { KassenApi } from '../api';
import {
  geraeteMeldung,
  ladeAutoDruck,
  oeffneLade,
  setzeAutoDruck,
  warteAufGeraeteauftrag,
  type GeraeteRueckmeldung,
} from '../bondruck';
import { istGueltigesPraefix, ladeScannerPraefix, setzeScannerPraefix } from '../scanner';

/**
 * Einrichtung der Geräte an dieser Kasse: automatischer Bondruck (Einstellung je Kasse, auf
 * diesem Gerät), Testbon und Kassenlade-Test für die Hardware-Abnahme. Bondrucker und Lade
 * hängen an der Hardware-Bridge dieser Kasse – Testbon und Lade müssen genau an diesem Platz
 * reagieren (Kreuztest mit der zweiten Kasse).
 */
export function GeraeteEinstellungen(props: {
  api: KassenApi;
  kassenId: string;
  bridgeZugelassen: boolean;
}) {
  const { api, kassenId } = props;
  const [autoDruck, setAutoDruck] = useState(() => ladeAutoDruck(localStorage, kassenId));
  const [laeuft, setLaeuft] = useState(false);
  const [meldung, setMeldung] = useState<GeraeteRueckmeldung | null>(null);
  /** PB-1: Scannerpräfix dieses Geräts (kurze Codes sicher als Scan erkennen). */
  const [praefix, setPraefix] = useState(() => ladeScannerPraefix(localStorage));
  const [praefixMeldung, setPraefixMeldung] = useState<string | null>(null);

  function praefixSetzen(neu: string | null): void {
    if (setzeScannerPraefix(localStorage, neu)) {
      setPraefix(neu);
      setPraefixMeldung(
        neu === null ? 'Kein Scannerpräfix eingerichtet.' : `Präfix ${neu} gespeichert.`,
      );
    } else {
      setPraefixMeldung(
        neu === null
          ? 'Gerätespeicher nicht beschreibbar.'
          : `Taste ${neu} ist als Präfix nicht geeignet – nur F8, F9 oder F13–F24 (am Scanner als Präfix einstellen).`,
      );
    }
  }

  async function pruefe(aktion: () => Promise<GeraeteRueckmeldung>): Promise<void> {
    setLaeuft(true);
    setMeldung({ ok: null, text: 'Auftrag an die Bridge übergeben …' });
    try {
      setMeldung(await aktion());
    } catch (e: unknown) {
      setMeldung({ ok: false, text: e instanceof Error ? e.message : String(e) });
    } finally {
      setLaeuft(false);
    }
  }

  return (
    <>
      <div className="karte">
        <h3>Bondrucker und Kassenlade</h3>
        {!props.bridgeZugelassen && (
          <p className="warnung">
            Für diese Kasse ist keine Hardware-Bridge eingerichtet (Bridge-Token im Backoffice
            ausstellen). Ohne Bridge druckt nur „Drucken“ über den Browser.
          </p>
        )}
        <p className="hinweis">
          Bondrucker und Kassenlade hängen an der Bridge der Kasse <strong>{kassenId}</strong>.
          Testbon und Lade müssen an genau diesem Platz reagieren – sonst ist die Bridge einer
          anderen Kasse zugeordnet.
        </p>
        <label style={{ flexDirection: 'row', alignItems: 'center', gap: '0.5rem' }}>
          <input
            type="checkbox"
            checked={autoDruck}
            disabled={!props.bridgeZugelassen}
            onChange={(e) => {
              setAutoDruck(e.target.checked);
              setzeAutoDruck(localStorage, kassenId, e.target.checked);
            }}
          />
          Bon nach jedem Verkauf automatisch drucken
        </label>
        <p className="hinweis">
          Jeder Beleg wird höchstens einmal automatisch gedruckt. „Nachdruck“ in der Belegansicht
          druckt denselben Beleg erneut mit dem Vermerk „KOPIE“ – es entsteht kein neuer Beleg.
        </p>
        <div className="zeile">
          <button
            type="button"
            className="taste zweit"
            disabled={laeuft || !props.bridgeZugelassen}
            onClick={() => {
              void pruefe(async () => {
                const a = await api.testdruck(kassenId);
                return geraeteMeldung(await warteAufGeraeteauftrag(api, kassenId, a), 'test');
              });
            }}
          >
            Testbon drucken
          </button>
          <button
            type="button"
            className="taste zweit"
            disabled={laeuft || !props.bridgeZugelassen}
            onClick={() => {
              void pruefe(() => oeffneLade(api, kassenId));
            }}
          >
            Kassenlade testen
          </button>
        </div>
        {meldung !== null && (
          <p
            className={meldung.ok === true ? 'ok' : meldung.ok === false ? 'fehler' : 'hinweis'}
            role="status"
          >
            {meldung.text}
          </p>
        )}
      </div>
      <div className="karte">
        <h3>Barcode-Scanner</h3>
        <p className="hinweis">
          Codes ab 4 Zeichen erkennt die Kasse am schnellen Tempo des Scanners. Kurze Codes (1–3
          Zeichen) gelten nur mit einem Präfix als Scan: Den Scanner so einstellen, dass er vor
          jedem Code dieselbe Funktionstaste sendet (F8, F9 oder F13–F24), und diese Taste hier
          einmal drücken. Ohne Präfix kurze Codes über die Suche eingeben.
        </p>
        <label>
          Scannerpräfix (dieses Gerät)
          <input
            readOnly
            value={praefix ?? ''}
            placeholder="Feld antippen, dann Taste drücken (z. B. F9)"
            aria-label="Scannerpräfix: Taste drücken"
            onKeyDown={(e) => {
              if (e.key === 'Tab' || e.key === 'Shift') {
                return;
              }
              e.preventDefault();
              if (istGueltigesPraefix(e.key) || e.key.length === 1 || /^F\d+$/.test(e.key)) {
                praefixSetzen(e.key);
              }
            }}
          />
        </label>
        <div className="zeile">
          <button
            type="button"
            className="taste zweit"
            disabled={praefix === null}
            onClick={() => {
              praefixSetzen(null);
            }}
          >
            Präfix entfernen
          </button>
        </div>
        {praefixMeldung !== null && (
          <p className="hinweis" role="status">
            {praefixMeldung}
          </p>
        )}
      </div>
    </>
  );
}
